import { cloneElement, isValidElement, useState, type CSSProperties, type MouseEvent, type ReactElement, type ReactNode } from "react";
import MuiPopover from "@mui/material/Popover";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// antd <Popover> -> MUI Popover anchored on the trigger child (click).
export interface PopoverProps {
  children: ReactElement;
  content?: ReactNode;
  title?: ReactNode;
  trigger?: "hover" | "click" | "focus" | Array<"hover" | "click" | "focus">;
  placement?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  overlayStyle?: CSSProperties;
  overlayClassName?: string;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
}
// antd placement -> MUI anchor/transform origins. `top*` opens ABOVE the trigger,
// `bottom*` (and the legacy default) BELOW; `*Right` right-aligns, else left. Without
// this the `placement` prop was ignored and everything hardcoded to bottom-left, so a
// `topRight` popover (e.g. the chat settings gear, pinned to the panel bottom) opened
// downward and MUI shoved it back up ON TOP of the trigger to keep it on-screen.
const placementOrigins = (placement?: string) => {
  if (!placement) {
    return { anchorOrigin: { vertical: "bottom", horizontal: "left" } } as const;
  }
  const vertical = placement.startsWith("top") ? "top" : "bottom";
  const horizontal = placement.endsWith("Right") ? "right" : "left";
  return {
    anchorOrigin: { vertical, horizontal },
    transformOrigin: { vertical: vertical === "top" ? "bottom" : "top", horizontal },
  } as const;
};

export const Popover = ({ children, content, title, placement, open, onOpenChange, overlayStyle }: PopoverProps) => {
  // Uncontrolled: anchor to the clicked trigger. Controlled (open prop set):
  // anchor to a wrapper node and mirror open via onOpenChange (antd parity).
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [wrapperNode, setWrapperNode] = useState<HTMLElement | null>(null);
  const isControlled = open !== undefined;
  const actualOpen = isControlled ? !!open : Boolean(anchorEl);
  const anchor = isControlled ? wrapperNode : anchorEl;
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void }>, {
        onClick: (e: MouseEvent<HTMLElement>) => {
          (children.props as { onClick?: (e: MouseEvent<HTMLElement>) => void }).onClick?.(e);
          if (!isControlled) setAnchorEl(e.currentTarget);
          onOpenChange?.(!actualOpen);
        },
      })
    : children;
  return (
    <>
      {isControlled ? (
        <span ref={setWrapperNode} style={{ display: "inline-flex" }}>
          {trigger}
        </span>
      ) : (
        trigger
      )}
      <MuiPopover
        open={actualOpen && (!isControlled || !!anchor)}
        anchorEl={anchor}
        onClose={() => { if (!isControlled) setAnchorEl(null); onOpenChange?.(false); }}
        // Backdrop click already fires onClose; the sluggish dismiss was the default
        // transitionDuration="auto", which scales the Grow exit to the content height
        // (~350ms for the tall chat-settings panel). Pin a snappy fixed duration so it
        // disappears on click-away instead of lingering.
        transitionDuration={{ enter: 200, exit: 120 }}
        // An anchored popover is not a dialog: MUI's default scroll-lock hides the
        // page scrollbar for as long as it is open, so the layout jumps sideways on
        // every open/close. antd's Popover (which this shims) never did that.
        disableScrollLock
        {...placementOrigins(placement)}
      >
        <Box sx={{ p: 2, ...(overlayStyle as object) }}>
          {title != null && <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>}
          {content}
        </Box>
      </MuiPopover>
    </>
  );
};

// antd <Popconfirm> -> MUI Popover with confirm/cancel buttons on the trigger.
export interface PopconfirmProps {
  children: ReactElement;
  title?: ReactNode;
  description?: ReactNode;
  okText?: string;
  cancelText?: string;
  okType?: string;
  onConfirm?: (e?: MouseEvent) => void;
  onCancel?: (e?: MouseEvent) => void;
  disabled?: boolean;
  placement?: string;
  okButtonProps?: { danger?: boolean; loading?: boolean; [k: string]: unknown };
  cancelButtonProps?: Record<string, unknown>;
}
export const Popconfirm = ({ children, title, description, okText = "OK", cancelText = "Cancel", okType, okButtonProps, onConfirm, onCancel, disabled }: PopconfirmProps) => {
  const danger = okType === "danger" || okButtonProps?.danger === true;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const close = () => setAnchorEl(null);
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void }>, {
        onClick: (e: MouseEvent<HTMLElement>) => {
          // Stop the trigger's click bubbling to a clickable ancestor (e.g. a
          // conversation-history row whose onClick loads the chat + closes the
          // menu — which made "delete" minimize the panel instead of deleting).
          // Also preserve the child's own onClick. Matches antd Popconfirm,
          // which swallows the trigger click.
          e.stopPropagation();
          (children.props as { onClick?: (e: MouseEvent<HTMLElement>) => void }).onClick?.(e);
          if (!disabled) setAnchorEl(e.currentTarget);
        },
      })
    : children;
  return (
    <>
      {trigger}
      <MuiPopover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} transformOrigin={{ vertical: "top", horizontal: "center" }}>
        <Box sx={{ p: 2, maxWidth: 300 }}>
          {title != null && <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>}
          {description != null && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
            <Button size="small" color="inherit" onClick={(e) => { onCancel?.(e); close(); }}>{cancelText}</Button>
            <Button size="small" variant="contained" color={danger ? "error" : "primary"} onClick={(e) => { onConfirm?.(e); close(); }}>{okText}</Button>
          </Box>
        </Box>
      </MuiPopover>
    </>
  );
};

// antd <Drawer> -> MUI Drawer.
export interface DrawerProps {
  children?: ReactNode;
  open?: boolean;
  /** antd v4 alias */
  visible?: boolean;
  onClose?: () => void;
  title?: ReactNode;
  placement?: "top" | "right" | "bottom" | "left";
  width?: number | string;
  height?: number | string;
  closable?: boolean;
  mask?: boolean;
  maskClosable?: boolean;
  drawerStyle?: CSSProperties;
  bodyStyle?: CSSProperties;
  /** antd v5 styles slot map (body/header/...). */
  styles?: { body?: CSSProperties; header?: CSSProperties; [k: string]: CSSProperties | undefined };
  style?: CSSProperties;
  className?: string;
}
// width default caps at the viewport so a right/left drawer never overflows a
// narrow screen (Modal already caps at 95vw; Drawer didn't).
export const Drawer = ({ children, open, visible, onClose, title, placement = "right", width = "min(378px, 100vw)", height, closable = true, className }: DrawerProps) => (
  <MuiDrawer anchor={placement} open={open ?? visible ?? false} onClose={onClose} className={className}>
    <Box sx={{ width: placement === "left" || placement === "right" ? width : "auto", height: placement === "top" || placement === "bottom" ? height : "auto", p: 2 }} role="presentation">
      {(title != null || closable) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          {closable && <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>}
        </Box>
      )}
      {children}
    </Box>
  </MuiDrawer>
);
